import { ArrowLeft, ArrowUpRight, Clock, Share2 } from "lucide-react";
import { Suspense, lazy } from "react";
import { useData } from "vike-react/useData";
import { Button } from "@/components/ui/button";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import ArticlePlaceholder from "@/components/blog/ArticlePlaceholder";
import ArticleImage from "@/components/blog/ArticleImage";
import { formatPublicationDate as formatDate } from "@/lib/date";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import type { ArticleSummary } from "@/server/db";
import type { BlogDetailData } from "./+data";

const CtaBanner = lazy(() => import("@/components/landing/CtaBanner"));

const CtaFallback = () => (
  <div className="h-48 w-full animate-pulse rounded-2xl bg-cream/60" />
);

const formatPublicationDate = (isoDateString: string | null): string =>
  formatDate(isoDateString, "long");

const initialsOf = (name: string | null): string => {
  if (!name) return "PK";
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export default function Page() {
  const { article, relatedArticles } = useData<BlogDetailData>();
  const { toast } = useToast();

  const fireShare = (method: "webshare" | "clipboard" | "error") =>
    trackEvent("share", {
      method,
      content_type: "article",
      content_id: article.article_slug,
      content_title: article.article_title,
    });

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = { title: article.article_title, url: shareUrl };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        fireShare("webshare");
        return;
      } catch (err) {
        // User cancelled the native share sheet — silent exit.
        if (err instanceof DOMException && err.name === "AbortError") return;
        // Other failures (permission, unsupported scheme) → fall through to clipboard.
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      fireShare("clipboard");
      toast({
        title: "Link disalin",
        description: "URL artikel sudah ada di clipboard kamu.",
      });
    } catch {
      fireShare("error");
      toast({
        title: "Gagal menyalin link",
        description: "Salin manual dari address bar browser.",
        variant: "destructive",
      });
    }
  };

  const handleRelatedClick = (related: ArticleSummary) => () =>
    trackEvent("select_content", {
      content_type: "article",
      item_id: related.article_slug,
      item_name: related.article_title,
      item_category: related.article_category ?? undefined,
      list_location: "related_articles",
      source_article: article.article_slug,
    });

  const breadcrumbItems = [
    { name: "Beranda", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: article.article_title, path: `/blog/${article.article_slug}` },
  ];

  // Per-article CTA overrides — NULL DB values become `undefined` so the
  // destructuring defaults inside CtaBanner kick in. Same overrides apply
  // to BOTH the in-article card and the page-level section banner so the
  // call-to-action stays consistent for the reader.
  const ctaOverrides = {
    eyebrow: article.cta_eyebrow ?? undefined,
    heading: article.cta_heading ?? undefined,
    description: article.cta_description ?? undefined,
    ctaLabel: article.cta_button_label ?? undefined,
  };

  return (
    <>
      <section className="relative isolate overflow-hidden bg-gradient-hero pb-16 pt-12 md:pt-20">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background/40 via-background/70 to-background" />

        <div className="container-narrow relative">
          <Breadcrumbs items={breadcrumbItems} className="mb-6" />

          <a
            href="/blog"
            className="group inline-flex items-center gap-2 text-mono text-xs uppercase tracking-wider text-muted-foreground transition-colors hover:text-primary"
            onClick={() =>
              trackEvent("nav_click", {
                nav_label: "kembali_ke_insights",
                nav_target: "/blog",
                location: "article_top",
              })
            }
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
            Kembali ke Insights
          </a>

          <div className="mx-auto mt-8 max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 text-mono text-xs uppercase tracking-wider text-muted-foreground">
              {article.article_category && (
                <span className="rounded-sm bg-primary/15 px-2 py-0.5 text-primary">
                  {article.article_category}
                </span>
              )}
              {article.read_minutes > 0 && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {article.read_minutes} min baca
                </span>
              )}
              {article.published_at && (
                <time dateTime={article.published_at}>
                  {formatPublicationDate(article.published_at)}
                </time>
              )}
            </div>

            <h1 className="heading-display mt-6 text-4xl leading-tight text-foreground sm:text-5xl md:text-6xl">
              {article.article_title}
            </h1>

            {article.article_excerpt && (
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
                {article.article_excerpt}
              </p>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-y border-border/60 py-5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-primary text-sm font-bold text-primary-foreground shadow-glow-primary">
                  {initialsOf(article.article_author_name)}
                </span>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {article.article_author_name ?? "Editorial"}
                  </div>
                  <div className="text-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Editorial Team
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="border-border bg-surface-elevated/50 backdrop-blur hover:bg-surface-raised"
                onClick={handleShare}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Bagikan
              </Button>
            </div>
          </div>
        </div>
      </section>

      {(article.article_thumbnail_url || article.article_thumbnail_mobile_url) && (
        <section className="bg-background">
          <div className="container-narrow">
            <div className="mx-auto max-w-4xl overflow-hidden rounded-md border border-border bg-surface-raised shadow-elevated">
              <ArticleImage
                src={article.article_thumbnail_url}
                mobileSrc={article.article_thumbnail_mobile_url}
                sizes="(min-width: 1024px) 1024px, 100vw"
                alt={article.article_title}
                width={1600}
                height={1000}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      <section className="bg-background py-16 md:py-20">
        <div className="container-narrow">
          <article className="mx-auto max-w-3xl">
            {/* Article body — raw HTML from articles.article_content rendered
                via Tailwind Typography. Brand-tuned theme is configured in
                tailwind.config.ts under `theme.extend.typography.DEFAULT.css`. */}
            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: article.article_content }}
            />

            <Suspense fallback={<CtaFallback />}>
              <CtaBanner variant="card" className="mt-16" {...ctaOverrides} />
            </Suspense>
          </article>
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="bg-surface-elevated py-20 md:py-24">
          <div className="container-narrow">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <div>
                <div className="text-mono text-sm uppercase tracking-widest text-primary">
                  // Lainnya
                </div>
                <h2 className="heading-display mt-3 text-3xl text-foreground sm:text-4xl">
                  Baca Juga
                </h2>
              </div>
              <a
                href="/blog"
                className="group inline-flex items-center gap-2 text-mono text-sm font-medium uppercase tracking-wider text-primary hover:text-primary-glow"
                onClick={() =>
                  trackEvent("nav_click", {
                    nav_label: "semua_artikel",
                    nav_target: "/blog",
                    location: "related_articles",
                  })
                }
              >
                Semua Artikel
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {relatedArticles.map((related) => (
                <article
                  key={related.id}
                  className="group relative flex flex-col overflow-hidden rounded-md border border-border bg-gradient-card shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-elevated"
                >
                  <a
                    href={`/blog/${related.article_slug}`}
                    className="block aspect-[16/10] overflow-hidden bg-surface-raised"
                    onClick={handleRelatedClick(related)}
                  >
                    {related.article_thumbnail_url || related.article_thumbnail_mobile_url ? (
                      <ArticleImage
                        src={related.article_thumbnail_url}
                        mobileSrc={related.article_thumbnail_mobile_url}
                        sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                        alt={related.article_title}
                        loading="lazy"
                        decoding="async"
                        width={800}
                        height={500}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <ArticlePlaceholder
                        slug={related.article_slug}
                        title={related.article_title}
                        className="transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                  </a>

                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-3 text-mono text-xs uppercase tracking-wider text-muted-foreground">
                      {related.article_category && (
                        <span className="rounded-sm bg-primary/15 px-2 py-0.5 text-primary">
                          {related.article_category}
                        </span>
                      )}
                      {related.read_minutes > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {related.read_minutes} min
                        </span>
                      )}
                    </div>

                    <h3 className="mt-4 text-lg font-bold leading-tight text-foreground transition-colors group-hover:text-primary">
                      <a
                        href={`/blog/${related.article_slug}`}
                        onClick={handleRelatedClick(related)}
                      >
                        {related.article_title}
                      </a>
                    </h3>

                    {related.article_excerpt && (
                      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                        {related.article_excerpt}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <Suspense fallback={<CtaFallback />}>
        <CtaBanner variant="section" {...ctaOverrides} />
      </Suspense>
    </>
  );
}
